import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'

import RankedItemCard from './RankedItemCard'

import { getTeamColor } from '../../utils/teamColors'

import FloatingText from './FloatingText'

export type Item = {
  question?: { sessionId: string; pollId: number; groupId: string; rankingVersion: number }
  title: string
  metric?: string
  rank?: number
  points: number
  index: number
  listId?: number
  team?: number
  guessed?: boolean
  pendingTeam?: number
  editable?: boolean
}

const ItemGuessBox = ({
  item,
  list,
  mode,
  hostMode,
  teamTurn,
  disabled,
  moved,
  onClick,
  fullScreen,
  showGameLoading,
  showSearch,
  voteParticipants,
  isVotePending,
  enableFeedback = true,
  enableHitFeedback = true,
  externalFeedback,
  externalFeedbackIndex,
  externalFeedbackNonce,
}: {
  item: Item
  list: Item[]
  mode: 'single' | 'multi' | 'host' | undefined
  hostMode: string
  teamTurn: number
  disabled?: boolean
  moved?: boolean
  onClick: () => void
  fullScreen?: boolean
  showGameLoading?: boolean
  showSearch?: boolean
  voteParticipants?: Array<{ id: string; icon: string; name: string }>
  isVotePending?: boolean
  enableFeedback?: boolean
  enableHitFeedback?: boolean
  externalFeedback?: {
    nonce: number
    label: string
    color?: string
  }
  externalFeedbackIndex?: number
  externalFeedbackNonce?: number
}) => {
  const [showText, setShowText] = useState(false)
  const [lastFloatingLabel, setLastFloatingLabel] = useState<string>('')
  const [lastMissLabel, setLastMissLabel] = useState<'Warm' | 'Cold' | ''>('')
  const [lastFloatingColor, setLastFloatingColor] = useState<string>('#9ca3af')
  const hideTextTimeoutRef = useRef<number | undefined>(undefined)

  const remaining = list
    .filter((item) => !item.team)
    .map((item) => item.index)
    .sort((a, b) => a - b)
  const minAvailable = remaining.length ? remaining[0] : item.index
  const isHit = item.index <= (minAvailable || 0) || (mode === 'host' && hostMode === 'any-order')
  const position = remaining.indexOf(item.index)
  const hasEnoughItems = remaining.length > 5
  const isWarm = hasEnoughItems && position >= 0 && position <= 2
  const isCold = hasEnoughItems && position >= remaining.length - 3
  const missLabel = isWarm ? 'Warm' : isCold ? 'Cold' : ''
  const floatingLabel = isHit
    ? mode !== 'single' && enableHitFeedback
      ? 'Nice! +1'
      : ''
    : missLabel
  const floatingColor = isHit
    ? getTeamColor(item.team ?? item.pendingTeam ?? teamTurn) || '#9ca3af'
    : missLabel === 'Warm'
      ? '#f59e0b'
      : missLabel === 'Cold'
        ? '#38bdf8'
        : '#9ca3af'

  // console.log(minAvailable, list)

  const handleClick = () => {
    // if pending item ignore
    if (list.find((l) => l.pendingTeam)?.pendingTeam) return

    if (!item.team && !disabled && !item.guessed) {
      onClick()
      if (enableFeedback) {
        setLastFloatingLabel(floatingLabel)
        setLastMissLabel(missLabel as 'Warm' | 'Cold' | '')
        setLastFloatingColor(floatingColor)
        setShowText(true)
        if (hideTextTimeoutRef.current) {
          window.clearTimeout(hideTextTimeoutRef.current)
        }
        hideTextTimeoutRef.current = window.setTimeout(() => {
          setShowText(false)
        }, 950)
      }
    }
  }

  useEffect(() => {
    if (!externalFeedback?.nonce || !externalFeedback.label) return
    setLastFloatingLabel(externalFeedback.label)
    setLastMissLabel(
      externalFeedback.label === 'Warm' || externalFeedback.label === 'Cold'
        ? externalFeedback.label
        : ''
    )
    setLastFloatingColor(externalFeedback.color || '#9ca3af')
    setShowText(true)
    if (hideTextTimeoutRef.current) {
      window.clearTimeout(hideTextTimeoutRef.current)
    }
    hideTextTimeoutRef.current = window.setTimeout(() => {
      setShowText(false)
    }, 950)
  }, [externalFeedback?.nonce])

  useEffect(() => {
    if (!externalFeedbackNonce) return
    if (externalFeedbackIndex !== item.index) {
      setShowText(false)
    }
  }, [externalFeedbackNonce, externalFeedbackIndex, item.index])

  useEffect(() => {
    return () => {
      if (hideTextTimeoutRef.current) {
        window.clearTimeout(hideTextTimeoutRef.current)
      }
    }
  }, [])

  const tone = item.team === 1 ? 'team1' : item.team === 2 ? 'team2' : 'neutral'

  const glowClass =
    item.guessed && !item.team
      ? lastMissLabel === 'Warm'
        ? 'miss-warm'
        : lastMissLabel === 'Cold'
          ? 'miss-cold'
          : 'miss-neutral'
      : undefined
  const cardClassName = [glowClass, moved ? 'card-enter' : null].filter(Boolean).join(' ')

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: disabled === true ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        filter: disabled ? 'saturate(0.7)' : 'none',
        borderRadius: '18px',
        border: isVotePending ? '2px solid rgba(2,158,224,0.65)' : '2px solid transparent',
        boxShadow: isVotePending ? '0 3px 0 #e0eaf2, 0 8px 18px #1c3b5709' : undefined,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            transform: disabled ? 'none' : 'translateY(-1px)',
          },
        },
        '&:active': {
          transform: disabled ? 'none' : 'scale(0.99)',
        },
        animation: [
          moved ? 'popIn 260ms ease-out' : null,
          showText ? 'tapBounce 180ms ease-out' : null,
        ]
          .filter(Boolean)
          .join(', '),
        '@keyframes popIn': {
          '0%': { opacity: 0, transform: 'translateY(14px) scale(0.98)' },
          '60%': { opacity: 1, transform: 'translateY(-2px) scale(1.01)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '@keyframes tapBounce': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(0.98)' },
          '100%': { transform: 'scale(1)' },
        },
      }}
      onClick={handleClick}
    >
      <RankedItemCard
        index={item.index}
        title={item.title}
        metric={(item.team || mode === 'host') && item.metric ? item.metric : undefined}
        tone={tone}
        dimmed={item.guessed && !item.team && !item.pendingTeam}
        showIndex={mode === 'host' ? true : !!item.team}
        fullScreen={fullScreen}
        className={cardClassName}
        loading={showGameLoading}
        rightAdornment={
          showSearch ? (
            <Tooltip title="Open search" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(
                    `https://www.google.com/search?q=${encodeURIComponent(item.title)}`,
                    '_blank'
                  )
                }}
                sx={{ opacity: 0.7 }}
              >
                <OpenInNewRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      />
      {voteParticipants?.length ? (
        <Box
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            pointerEvents: 'none',
          }}
        >
          <AvatarGroup
            max={4}
            sx={{
              '& .MuiAvatar-root': {
                width: 24,
                height: 24,
                fontSize: 12,
                border: '1px solid rgba(255,255,255,0.9)',
              },
            }}
          >
            {voteParticipants.map((participant) => (
              <Avatar key={`vote-participant-${participant.id}`}>{participant.icon}</Avatar>
            ))}
          </AvatarGroup>
        </Box>
      ) : null}

      {showText && (
        <div
          style={{
            width: '100%',
            height: '100%',
            overflow: 'visible',
            position: 'relative',
            color: lastFloatingColor,
          }}
        >
          <Box
            sx={{
              width: '0',
              height: '0',
              position: 'absolute',
              top: '-30px',
              right: '100px',
              color: lastFloatingColor,
              fontSize: '26px',
            }}
          >
            <FloatingText
              style={{
                animationDuration:
                  lastMissLabel === 'Warm' ? '1.4s' : lastMissLabel === 'Cold' ? '1s' : '1.1s',
              }}
              className={
                lastMissLabel === 'Warm'
                  ? 'float-warm'
                  : lastMissLabel === 'Cold'
                    ? 'float-cold'
                    : 'float-miss'
              }
            >
              {lastFloatingLabel}
            </FloatingText>
          </Box>
        </div>
      )}
    </Box>
  )
}

export default ItemGuessBox
